import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  SafetyOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import {
  App,
  Button,
  Card,
  Checkbox,
  Col,
  Divider,
  Form,
  Input,
  Modal,
  Popconfirm,
  Row,
  Space,
  Spin,
  Table,
  Tag,
  theme,
  Tooltip,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { getAllMenus } from '../../services/menuService';


const { TextArea } = Input;
const { Title, Text } = Typography;

const RolesList = () => {
  const { token } = theme.useToken();
  const [roles, setRoles] = useState<any[]>([]);
  const [permissions, setPermissions] = useState<any[]>([]);
  const [menus, setMenus] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRole, setEditingRole] = useState<any>(null);
  const [form] = Form.useForm();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [filters, setFilters] = useState({
    search: '',
  });
  const [checkedKeys, setCheckedKeys] = useState<string[]>([]);
  const [submitLoading, setSubmitLoading] = useState(false);
  const { message } = App.useApp();
  const { hasPermission } = useAuth();

  const canCreate = hasPermission('roles.create');
  const canUpdate = hasPermission('roles.update');
  const canDelete = hasPermission('roles.delete');

  const fetchRoles = useCallback(async (currentFilters: typeof filters, currentPage: number, currentPageSize: number) => {
    setLoading(true);
    try {
      const params: { [key: string]: any } = {
        page: currentPage,
        limit: currentPageSize,
        search: currentFilters.search,
      };

      Object.keys(params).forEach(key => {
        if (params[key] === null || params[key] === undefined || params[key] === '') {
          delete params[key];
        }
      });

      const rolesRes = await api.get('roles', { params });
      setRoles(rolesRes.data.data || []);
      setPagination(prev => ({
        ...prev,
        total: rolesRes.data.meta?.total || rolesRes.data.data?.length || 0,
        current: currentPage,
        pageSize: currentPageSize,
      }));
    } catch (error: any) {
      console.error('Failed to fetch roles:', error);
      message.error('Gagal memuat data role');
    } finally {
      setLoading(false);
    }
  }, [message]);

  useEffect(() => {
    const fetchStaticData = async () => {
      try {
        const menusRes = await getAllMenus();
        const allPerms = extractPermissionsFromMenus(menusRes.data.data || []);
        setPermissions(allPerms);
        setMenus(menusRes.data.data || []);
      } catch (error) {
        console.error('Failed to fetch menus/permissions:', error);
        message.error('Gagal memuat data permission');
      }
    };
    fetchStaticData();
  }, [message]);

  const { current: currentPage, pageSize } = pagination;

  useEffect(() => {
    fetchRoles(filters, currentPage, pageSize);
  }, [filters, currentPage, pageSize, fetchRoles]);

  useEffect(() => {
    if (modalVisible) {
      if (editingRole) {
        const permissionIds = editingRole.permissions?.map((p: any) => String(p.id)) || [];
        setCheckedKeys(permissionIds);
        form.setFieldsValue({
          name: editingRole.name,
          display_name: editingRole.display_name,
          description: editingRole.description,
          permission_ids: permissionIds,
        });
      } else {
        setCheckedKeys([]);
        form.resetFields();
      }
    }
  }, [modalVisible, editingRole, form]);

  const handleTableChange = (newPagination: any) => {
    setPagination(prev => ({
      ...prev,
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    }));
  };

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        setFilters(prev => ({ ...prev, search: value }));
        setPagination(prev => ({ ...prev, current: 1 }));
      }, 500),
    []
  );

  const extractPermissionsFromMenus = (menus: any[]): any[] => {
    let permissions: any[] = [];
    const traverse = (menuItems: any[]) => {
      menuItems.forEach(menu => {
        if (menu.Permissions) {
          permissions = [...permissions, ...menu.Permissions];
        }
        if (menu.Children) {
          traverse(menu.Children);
        }
      });
    };
    traverse(menus);
    // Remove duplicates
    return permissions.filter((p, index, self) =>
      index === self.findIndex((t) => (t.id === p.id))
    );
  };

  const handleDelete = async (id: any) => {
    try {
      await api.delete(`roles/${id}`);
      message.success('Role berhasil dihapus');
      fetchRoles(filters, pagination.current, pagination.pageSize);
    } catch (error: any) {
      console.error('Failed to delete role:', error);
      const errorMsg = error.response?.data?.message || 'Gagal menghapus role';
      message.error(errorMsg);
    }
  };

  const handleModalOk = async () => {
    try {
      setSubmitLoading(true);
      const values = await form.validateFields();

      const formData = {
        name: values.name,
        display_name: values.display_name,
        description: values.description || '',
        permission_ids: checkedKeys,
      };

      console.log('Sending data to API:', formData);
      console.log('permission_ids is array:', Array.isArray(formData.permission_ids));
      console.log('permission_ids length:', formData.permission_ids.length);

      if (editingRole) {
        await api.put(`roles/${editingRole.id}`, formData);
        message.success('Role berhasil diperbarui');
      } else {
        await api.post('roles', formData);
        message.success('Role berhasil dibuat');
      }

      setModalVisible(false);
      setEditingRole(null);
      setCheckedKeys([]);
      form.resetFields();
      fetchRoles(filters, pagination.current, pagination.pageSize);
    } catch (error: any) {
      console.error('Failed to save role:', error);
      console.error('Error details:', error.response?.data);
      const errorMsg = error.response?.data?.message || 'Gagal menyimpan role';
      message.error(errorMsg);
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleModalCancel = () => {
    setModalVisible(false);
    setEditingRole(null);
  };

  const handleEdit = (role: any) => {
    setEditingRole(role);
    setModalVisible(true);
  };

  const handleAdd = () => {
    setEditingRole(null);
    setModalVisible(true);
  };

  const onPermissionChange = (permissionId: string, checked: boolean) => {
    let newCheckedKeys;
    if (checked) {
      newCheckedKeys = [...checkedKeys, permissionId];
    } else {
      newCheckedKeys = checkedKeys.filter(id => id !== permissionId);
    }
    setCheckedKeys(newCheckedKeys);
    form.setFieldValue('permission_ids', newCheckedKeys);
  };

  const getAllPermissionIdsFromMenu = (menu: any): string[] => {
    let ids: string[] = [];
    if (menu.Permissions) {
      ids = [...ids, ...menu.Permissions.map((p: any) => p.id)];
    }
    if (menu.Children) {
      menu.Children.forEach((child: any) => {
        ids = [...ids, ...getAllPermissionIdsFromMenu(child)];
      });
    }
    return ids;
  };

  const handleSelectAllModule = (menu: any, checked: boolean) => {
    const permissionIds = getAllPermissionIdsFromMenu(menu);
    let newCheckedKeys;
    if (checked) {
      newCheckedKeys = Array.from(new Set([...checkedKeys, ...permissionIds]));
    } else {
      newCheckedKeys = checkedKeys.filter(id => !permissionIds.includes(id));
    }
    setCheckedKeys(newCheckedKeys);
    form.setFieldValue('permission_ids', newCheckedKeys);
  };

  const isModuleFullySelected = (menu: any) => {
    const permissionIds = getAllPermissionIdsFromMenu(menu);
    if (permissionIds.length === 0) return false;
    return permissionIds.every((id: string) => checkedKeys.includes(id));
  };

  const isModulePartiallySelected = (menu: any) => {
    const permissionIds = getAllPermissionIdsFromMenu(menu);
    if (permissionIds.length === 0) return false;
    const hasSome = permissionIds.some((id: string) => checkedKeys.includes(id));
    return hasSome && !isModuleFullySelected(menu);
  };

  const renderPermissionsRecursive = (menus: any[], level = 0) => {
    return menus.map((menu) => (
      <React.Fragment key={menu.id}>
        <div
          style={{
            marginLeft: level * 24,
            padding: '12px 8px',
            backgroundColor: level === 0 ? '#fafafa' : 'transparent',
          }}
        >
          <Text strong style={{ fontSize: 15 }}>{menu.Name}</Text>
        </div>

        {menu.Permissions && menu.Permissions.length > 0 && (
          <div style={{ marginLeft: level * 24 + 24, padding: '0 8px 12px 8px' }}>
            <Row align="middle" gutter={[16, 8]}>
              <Col>
                <Checkbox
                  indeterminate={isModulePartiallySelected(menu)}
                  checked={isModuleFullySelected(menu)}
                  onChange={(e) => handleSelectAllModule(menu, e.target.checked)}
                >
                  <Text strong>Select All</Text>
                </Checkbox>
              </Col>
              {menu.Permissions.map((p: any) => (
                <Col key={p.id}>
                  <Checkbox
                    value={p.id}
                    checked={checkedKeys.includes(p.id)}
                    onChange={(e) => onPermissionChange(p.id, e.target.checked)}
                  >
                    {p.display_name}
                  </Checkbox>
                </Col>
              ))}
            </Row>
          </div>
        )}

        {level > 0 && <Divider style={{ margin: '0' }} />}

        {menu.Children && renderPermissionsRecursive(menu.Children, level + 1)}
      </React.Fragment>
    ));
  };

  const columns: any[] = [
    {
      title: 'Role Info',
      key: 'role_info',
      width: 300,
      render: (_: any, record: any) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <SafetyOutlined
            style={{
              fontSize: 24,
              color: token.colorSuccess,
              marginRight: 12,
              padding: 8,
              background: '#f0f9ff',
              borderRadius: 8,
            }}
          />
          <div>
            <div style={{ fontWeight: 'bold', marginBottom: 4 }}>{record.display_name}</div>
            <div style={{ color: '#666', fontSize: 12 }}>@{record.name}</div>
          </div>
        </div>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (description: string) => description || '-',
    },
    {
      title: 'Permissions',
      key: 'permissions',
      render: (_: any, record: any) => (
        <div>
          {record.permissions?.slice(0, 3).map((permission: any) => (
            <Tag key={permission.id} style={{ marginBottom: 4 }}>
              {permission.display_name}
            </Tag>
          ))}
          {record.permissions?.length > 3 && (
            <Tag>+{record.permissions.length - 3} more</Tag>
          )}
          {(!record.permissions || record.permissions.length === 0) && (
            <Text type="secondary">No permissions</Text>
          )}
        </div>
      ),
    },

    {
      title: 'Tanggal Dibuat',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => date ? dayjs(date).format('DD MMM YYYY') : '-',
      sorter: (a: any, b: any) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    },
    {
      title: 'Aksi',
      key: 'action',
      fixed: 'right',
      width: 120,
      render: (_: any, record: any) => (
        <Space>
          {canUpdate && (
            <Tooltip title="Edit">
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => handleEdit(record)}
              />
            </Tooltip>
          )}
          {canDelete && !['admin', 'user'].includes(record.name) && (
            <Popconfirm
              title="Yakin ingin menghapus role ini?"
              description="Role yang sudah dihapus tidak dapat dikembalikan."
              onConfirm={() => handleDelete(record.id)}
              okText="Ya"
              cancelText="Tidak"
            >
              <Tooltip title="Hapus">
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>
            Manajemen Role
          </Title>
          <Text type="secondary">Kelola role dan permissions untuk pengguna sistem</Text>
        </Col>
        <Col>
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => fetchRoles(filters, pagination.current, pagination.pageSize)}
              disabled={loading}
            >
              Refresh
            </Button>
            {canCreate && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAdd}
              >
                Tambah Role
              </Button>
            )}
          </Space>
        </Col>
      </Row>

      <Card>
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={8}>
            <Input
              placeholder="Cari nama role..."
              prefix={<SearchOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
              onChange={(e) => debouncedSearch(e.target.value)}
              allowClear
            />
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={roles}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} dari ${total} roles`,
          }}
          onChange={handleTableChange}
          scroll={{ x: 1200 }}
          size="middle"
        />
      </Card>

      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <SafetyOutlined style={{ marginRight: 8, color: token.colorSuccess }} />
            {editingRole ? 'Edit Role' : 'Tambah Role'}
          </div>
        }
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        width={900}
        style={{ top: 20 }}
        destroyOnClose={true}
        confirmLoading={submitLoading}
        okText={editingRole ? 'Update' : 'Simpan'}
        cancelText="Batal"
      >
        <Spin spinning={submitLoading}>
          <Form
            form={form}
            layout="vertical"
          >
            <Row gutter={[16, 0]}>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Role Name (System)"
                  name="name"
                  rules={[
                    { required: true, message: 'Role name wajib diisi' },
                    { pattern: /^[a-z_]+$/, message: 'Gunakan huruf kecil dan underscore saja' }
                  ]}
                  tooltip="Nama sistem untuk role, gunakan format snake_case"
                >
                  <Input
                    placeholder="e.g. sales_manager"
                    disabled={editingRole && ['admin', 'user'].includes(editingRole.name)}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Display Name"
                  name="display_name"
                  rules={[{ required: true, message: 'Display name wajib diisi' }]}
                  tooltip="Nama yang akan ditampilkan di interface"
                >
                  <Input placeholder="e.g. Sales Manager" />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item
                  label="Description"
                  name="description"
                  tooltip="Deskripsi singkat tentang role ini"
                >
                  <TextArea
                    rows={3}
                    placeholder="Deskripsi role..."
                    maxLength={255}
                    showCount
                  />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item
                  label={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                      <span>Permissions</span>
                      <Text type="secondary">
                        {checkedKeys.length} dari {permissions.length} dipilih
                      </Text>
                    </div>
                  }
                  name="permission_ids"
                  rules={[
                    {
                      validator: (_, value) => {
                        if (!checkedKeys || checkedKeys.length === 0) {
                          return Promise.reject(new Error('Pilih minimal satu permission'));
                        }
                        return Promise.resolve();
                      }
                    }
                  ]}
                >
                  <div style={{
                    maxHeight: 400,
                    overflow: 'auto',
                    border: '1px solid #d9d9d9',
                    borderRadius: 8,
                    padding: '16px',
                    width: '100%',
                  }}>
                    {menus.length > 0 ? (
                      <div>
                        {menus.map((menu) => (
                          <div
                            key={menu.id}
                            style={{
                              border: '1px solid #e8e8e8',
                              borderRadius: 8,
                              marginBottom: 16,
                              overflow: 'hidden',
                            }}
                          >
                            {renderPermissionsRecursive([menu])}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ textAlign: 'center', padding: 32 }}>
                        <Spin />
                        <Text> Loading Permissions...</Text>
                      </div>
                    )}
                  </div>
                </Form.Item>

              </Col>
            </Row>
          </Form>
        </Spin>
      </Modal>
    </div>
  );
};

export default RolesList;
